import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CottageOutlinedIcon from "@mui/icons-material/CottageOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useNavigate, useParams } from "react-router-dom";
import ClientsList from "../../components/admin/ClientsList";
import UserApprovalsList from "../../components/admin/UserApprovalsList";
import AnnouncementsList from "../../components/admin/AnnouncementsList";
import { fetchUserByIdAction } from "../../ustils/actioncreator";
import PageHeader from "../../components/admin/PageHeader";
import AdminTabBar from "../../components/admin/AdminTabBar";
import MasterDataSettings from "../../components/admin/MasterDataSettings";
import GuestAnnouncementsList from "../../components/admin/GuestAnnouncementsList";
import { useAdminUi } from "../../hooks/useAdminUi";

function unwrapUserDetail(res) {
  if (!res || typeof res !== "object") return null;
  if (res._id || res.id) return res;
  const inner = res.data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    if (inner._id || inner.id || inner.email != null) return inner;
    if (inner.user && typeof inner.user === "object") return inner.user;
  }
  return null;
}

function formatApiDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return String(iso);
  }
}

function isBrokerUser(u) {
  if (!u) return false;
  return u.role === 2 || u.role === "2" || u.isBrokker === true;
}

function formatList(arr) {
  if (!Array.isArray(arr) || !arr.length) return "";
  return arr.filter(Boolean).join(", ");
}

function DetailRow({ label, value }) {
  const { ui } = useAdminUi();
  const text = value === undefined || value === null || value === "" ? "—" : String(value);
  const labelText = String(label || "").replace(/\s*:\s*$/, "").trim();
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={1.5}
      sx={{
        py: 0.75,
        gap: 1,
        borderBottom: `1px solid ${ui.borderLight}`,
        "&:last-of-type": { borderBottom: "none", pb: 0 },
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          color: ui.inkSoft,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          minWidth: 108,
          flexShrink: 0,
          lineHeight: 1.35,
        }}
      >
        {labelText}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          color: ui.ink,
          fontWeight: 500,
          textAlign: "right",
          wordBreak: "break-word",
          lineHeight: 1.45,
        }}
      >
        {text}
      </Typography>
    </Stack>
  );
}

function DocThumb({ label, src }) {
  const { ui, placeholderBoxSx } = useAdminUi();
  const labelText = String(label || "").replace(/\s*:\s*$/, "").trim();
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography sx={{ fontSize: 11, color: ui.inkSoft, mb: 0.6, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {labelText}
      </Typography>
      {src ? (
        <Box
          component="img"
          src={src}
          alt=""
          sx={{
            width: "100%",
            maxHeight: 128,
            objectFit: "cover",
            border: `1px solid ${ui.border}`,
            borderRadius: 2,
            boxShadow: ui.shadowSm,
            display: "block",
          }}
        />
      ) : (
        <Box
          sx={{
            height: 52,
            ...placeholderBoxSx,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: 11, color: ui.inkSoft }}>No file</Typography>
        </Box>
      )}
    </Box>
  );
}

function OverviewSectionTitle({ children }) {
  const { ui } = useAdminUi();
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
      <Box sx={{ width: 4, height: 22, borderRadius: 1, bgcolor: ui.gold, flexShrink: 0 }} />
      <Typography
        component="h2"
        sx={{
          fontSize: { xs: 17, md: 19 },
          fontWeight: 600,
          color: ui.ink,
          letterSpacing: "-0.02em",
          lineHeight: 1.25,
        }}
      >
        {children}
      </Typography>
    </Stack>
  );
}

const cards = [
  { title: "Buy Property", sub: "0 Property Buy", color: "#d6bf74", icon: <CottageOutlinedIcon fontSize="small" /> },
  { title: "Sell Property", sub: "0 Property Sell", color: "#e98d8d", icon: <SellOutlinedIcon fontSize="small" /> },
  { title: "Rent Property", sub: "0 Property Rent", color: "#74c28f", icon: <HomeOutlinedIcon fontSize="small" /> },
];

function ActionIcons() {
  const { ui } = useAdminUi();
  return (
    <Stack direction="row" spacing={0.8}>
      <RemoveRedEyeOutlinedIcon sx={{ fontSize: 16, color: ui.iconView, cursor: "pointer" }} />
      <EditOutlinedIcon sx={{ fontSize: 16, color: ui.iconEdit, cursor: "pointer" }} />
      <DeleteOutlineOutlinedIcon sx={{ fontSize: 16, color: ui.iconDelete, cursor: "pointer" }} />
    </Stack>
  );
}

export function DashboardHomePage() {
  const { pageWrapSx, statCardSx, ui } = useAdminUi();
  return (
    <Box sx={pageWrapSx}>
      <PageHeader title="Dashboard" crumbs={["Dashboard"]} />
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        {cards.map((card) => (
          <Paper key={card.title} elevation={0} sx={statCardSx(card.color)}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box className="stat-icon">{card.icon}</Box>
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2, color: ui.ink }}>{card.title}</Typography>
                <Typography sx={{ fontSize: 12, color: ui.inkSoft, mt: 0.25 }}>{card.sub}</Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}

export function CustomersClientsPage() {
  const { pageWrapSx } = useAdminUi();
  return (
    <Box sx={pageWrapSx}>
      <PageHeader title="Customers" crumbs={["Customers", "Clients"]} />
      <ClientsList />
    </Box>
  );
}

function OverviewLayout({ activity }) {
  const {
    pageWrapSx,
    tableBodyCellSx,
    tableCardSx,
    tableHeadRowSx,
    ui,
    sectionLabelSx,
    overviewMainCardSx,
    panelMutedBarSx,
    panelSectionBarSx,
    placeholderBoxSx,
    tableRowHoverSx,
    tableContainerSx,
    tableSx,
  } = useAdminUi();
  const navigate = useNavigate();
  const { userId } = useParams();
  const [selectedProfileTab, setSelectedProfileTab] = useState("user");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const basePath = userId ? `/dashboard/customers/overview/${userId}` : "/dashboard/customers/clients";
  const brokerUser = isBrokerUser(user);

  useEffect(() => {
    setSelectedProfileTab("user");
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setLoadError("");
    (async () => {
      try {
        const raw = await fetchUserByIdAction(userId);
        if (cancelled) return;
        const u = unwrapUserDetail(raw);
        if (!u) setLoadError(raw?.message || "User not found.");
        else setUser(u);
      } catch (e) {
        if (!cancelled) setLoadError(e?.response?.data?.message || e?.message || "Failed to load user.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!brokerUser && selectedProfileTab === "broker") setSelectedProfileTab("user");
  }, [brokerUser, selectedProfileTab]);

  const metricCard = (title, subtitle, tone) => (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${ui.border}`,
        borderRadius: `${ui.tableCardRadius}px`,
        p: 1.2,
        width: { xs: "100%", sm: 200 },
        minWidth: 0,
        bgcolor: ui.surface,
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          boxShadow: ui.shadowMd,
          transform: "translateY(-2px)",
        },
      }}
    >
      <Stack direction="row" spacing={1.15} alignItems="center">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: `${tone}18`,
            color: tone,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <CottageOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.2, fontWeight: 600, color: ui.ink }}>{title}</Typography>
          <Typography sx={{ fontSize: 11, color: ui.inkSoft, mt: 0.25 }}>{subtitle}</Typography>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={pageWrapSx}>
      <PageHeader title="Customer Overview" crumbs={["Customers", "Clients", "Customer Overview"]} />
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress size={32} />
        </Box>
      ) : null}
      {loadError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {loadError}
        </Alert>
      ) : null}
      {!loading && !loadError && user ? (
        <Paper elevation={0} sx={overviewMainCardSx}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
            sx={{
              px: { xs: 1.5, sm: 2.25 },
              py: { xs: 1.5, sm: 1.75 },
              bgcolor: ui.panelHeaderBg,
              borderBottom: `1px solid ${ui.borderLight}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: { xs: "100%", sm: "auto" } }}>
              <IconButton
                size="small"
                onClick={() => navigate("/dashboard/customers/clients")}
                aria-label="Back to clients"
                sx={{
                  border: `1px solid ${ui.border}`,
                  bgcolor: ui.surface,
                  color: ui.inkMuted,
                  "&:hover": {
                    bgcolor: ui.successChipBg,
                    borderColor: ui.green,
                    color: ui.greenDark,
                  },
                }}
              >
                <ArrowBackOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...sectionLabelSx, mb: 0 }}>Customer profile</Typography>
                <Typography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 700, color: ui.inkStrong, lineHeight: 1.2, mt: 0.25 }}>
                  {user.name || "—"}
                </Typography>
              </Box>
            </Stack>
            <Chip
              label={!brokerUser ? "User" : selectedProfileTab === "broker" ? "Broker" : "User / Broker"}
              size="small"
              sx={{
                ml: { xs: 0, sm: "auto" },
                height: 28,
                fontSize: 12,
                fontWeight: 600,
                bgcolor: ui.successChipBg,
                color: ui.successChipColor,
                border: `1px solid ${ui.successChipBorder}`,
              }}
            />
          </Stack>

          <Box sx={panelMutedBarSx}>
            <AdminTabBar
              tabs={[
                { id: "user", label: "User" },
                ...(brokerUser ? [{ id: "broker", label: "Broker" }] : []),
              ]}
              value={selectedProfileTab}
              onChange={setSelectedProfileTab}
            />
          </Box>

          {selectedProfileTab !== "broker" ? (
            <Box sx={{ ...panelMutedBarSx, py: 1.25 }}>
              <AdminTabBar
                variant="primary"
                tabs={[
                  { id: "info", label: "Info" },
                  { id: "activity", label: "Activity" },
                ]}
                value={activity ? "activity" : "info"}
                onChange={(id) => navigate(id === "activity" ? `${basePath}/activity` : `${basePath}/info`)}
              />
            </Box>
          ) : null}

          <Box sx={{ p: { xs: 1.5, sm: 2.25 } }}>
            {!activity ? (
              <>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
                  <Paper
                    elevation={0}
                    sx={{
                      width: { xs: "100%", md: 280 },
                      flexShrink: 0,
                      borderRadius: 2.5,
                      border: `1px solid ${ui.border}`,
                      overflow: "hidden",
                      bgcolor: ui.surface,
                      boxShadow: ui.shadowSm,
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.75,
                        py: 1.5,
                        bgcolor: ui.profileCardHeaderBg,
                        borderBottom: `1px solid ${ui.borderLight}`,
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar
                          src={user.userProfileImage || undefined}
                          sx={{
                            width: 64,
                            height: 64,
                            fontSize: 22,
                            fontWeight: 600,
                            border: `3px solid ${ui.avatarBorder}`,
                            boxShadow: ui.shadowSm,
                          }}
                        >
                          {(user.name || "?")[0]}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 15, fontWeight: 700, color: ui.inkStrong, lineHeight: 1.25 }}>{user.name || "—"}</Typography>
                          <Typography sx={{ fontSize: 12, color: ui.inkMuted, mt: 0.35 }}>Member overview</Typography>
                        </Box>
                      </Stack>
                    </Box>
                    <Box sx={{ p: 1.75 }}>
                      <Typography sx={{ ...sectionLabelSx, mb: 1 }}>Contact & account</Typography>
                      <DetailRow
                        label="Email :"
                        value={selectedProfileTab === "broker" ? user.professionalEmail || user.email : user.email}
                      />
                      <DetailRow label="Phone :" value={[user.countryCode, user.mobileNumber].filter(Boolean).join(" ").trim()} />
                      <DetailRow label="Country :" value={user.dealingCountry} />
                      {selectedProfileTab === "user" ? (
                        <>
                          <Divider sx={{ my: 1.25, borderColor: ui.borderLight }} />
                          <Typography sx={{ ...sectionLabelSx, mb: 1 }}>
                            Verification
                          </Typography>
                          <DetailRow label="Verification status :" value={user.verificationStatus} />
                          <DetailRow
                            label="Email verified :"
                            value={user.isEmailVerified === true ? "Yes" : user.isEmailVerified === false ? "No" : ""}
                          />
                          <DetailRow label="Role :" value={user.role} />
                          <DetailRow label="Current role :" value={user.currentRole} />
                          <DetailRow label="Account type :" value={user.account_type} />
                          <DetailRow label="Status :" value={user.status} />
                          <DetailRow label="Member since :" value={formatApiDate(user.created_at)} />
                          <DetailRow label="Last updated :" value={formatApiDate(user.updated_at)} />
                        </>
                      ) : null}
                      {selectedProfileTab === "broker" && brokerUser ? (
                        <>
                          <Divider sx={{ my: 1.25, borderColor: ui.borderLight }} />
                          <Typography sx={{ ...sectionLabelSx, mb: 1 }}>
                            Broker details
                          </Typography>
                          <DetailRow label="City where dealing :" value={formatList(user.dealingCities)} />
                          <DetailRow
                            label="Experience :"
                            value={user.experience != null && user.experience !== "" ? `${user.experience} Years` : ""}
                          />
                          <DetailRow label="BNR :" value={user.bnrNumber} />
                          <DetailRow label="ONR :" value={user.onrNumber} />
                          <DetailRow label="Languages :" value={formatList(user.knownLanguages)} />
                          <DetailRow label="Dealing Areas :" value={formatList(user.dealingAreas)} />
                          <DetailRow label="Verification status :" value={user.verificationStatus} />
                          <DetailRow label="Account type :" value={user.account_type} />
                          <Divider sx={{ my: 1.25, borderColor: ui.borderLight }} />
                          <Typography sx={{ ...sectionLabelSx, mb: 1 }}>
                            Documents
                          </Typography>
                          <DocThumb label="Passport :" src={user.passportImage} />
                          <Typography sx={{ ...sectionLabelSx, mb: 0.6, letterSpacing: "0.04em" }}>Local ID</Typography>
                          <Stack direction="row" spacing={1}>
                            {user.localIdFrontImage ? (
                              <Box
                                component="img"
                                src={user.localIdFrontImage}
                                alt=""
                                sx={{
                                  height: 80,
                                  flex: 1,
                                  objectFit: "cover",
                                  border: `1px solid ${ui.border}`,
                                  borderRadius: 2,
                                  boxShadow: ui.shadowSm,
                                }}
                              />
                            ) : (
                              <Box sx={{ height: 48, flex: 1, ...placeholderBoxSx }} />
                            )}
                            {user.localIdBackImage ? (
                              <Box
                                component="img"
                                src={user.localIdBackImage}
                                alt=""
                                sx={{
                                  height: 80,
                                  flex: 1,
                                  objectFit: "cover",
                                  border: `1px solid ${ui.border}`,
                                  borderRadius: 2,
                                  boxShadow: ui.shadowSm,
                                }}
                              />
                            ) : (
                              <Box sx={{ height: 48, flex: 1, ...placeholderBoxSx }} />
                            )}
                          </Stack>
                        </>
                      ) : null}
                    </Box>
                  </Paper>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <OverviewSectionTitle>{selectedProfileTab === "broker" ? "Own deals" : "Deals"}</OverviewSectionTitle>
                    <Stack direction="row" spacing={1.25} mb={2} flexWrap="wrap" useFlexGap>
                      {metricCard(selectedProfileTab === "broker" ? "Completed Deals" : "Parched Property", selectedProfileTab === "broker" ? "0 Deal" : "0 Property Buy", "#d1be7d")}
                      {metricCard(selectedProfileTab === "broker" ? "InProgress Deals" : "Sold Property", selectedProfileTab === "broker" ? "0 Deal" : "0 Property Sell", "#e39394")}
                      {metricCard(selectedProfileTab === "broker" ? "Cancelled Deals" : "Rented Property", selectedProfileTab === "broker" ? "0 Deal" : "0 Property Rent", "#79c390")}
                    </Stack>
                    {selectedProfileTab === "broker" ? (
                      <>
                        <OverviewSectionTitle>Platform deals</OverviewSectionTitle>
                        <Stack direction="row" spacing={1.25} mb={2} flexWrap="wrap" useFlexGap>
                          {metricCard("Completed Deals", "0 Deal", "#d1be7d")}
                          {metricCard("InProgress Deals", "0 Deal", "#e39394")}
                          {metricCard("Cancelled Deals", "0 Deal", "#79c390")}
                        </Stack>
                      </>
                    ) : null}
                    <OverviewSectionTitle>{selectedProfileTab === "broker" ? "Own announcement" : "Announcements"}</OverviewSectionTitle>
                    <Stack direction="row" spacing={1.25} mb={2} flexWrap="wrap" useFlexGap>
                      {metricCard("Sell Announcement", "0 Property Sell", "#e39394")}
                      {metricCard("Rent Announcement", "0 Property Rent", "#79c390")}
                      {selectedProfileTab === "broker" ? null : metricCard("Rejected Announcement", "0 Property Rejected", "#79c390")}
                    </Stack>
                    {selectedProfileTab === "broker" ? (
                      <>
                        <OverviewSectionTitle>User announcement</OverviewSectionTitle>
                        <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                          {metricCard("Sell Announcement", "0 Property Sell", "#e39394")}
                          {metricCard("Rent Announcement", "0 Property Rent", "#79c390")}
                        </Stack>
                      </>
                    ) : (
                      <>
                        <OverviewSectionTitle>Interests</OverviewSectionTitle>
                        <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                          {metricCard("Property Interests", "231 Property View", "#7eb4e2")}
                        </Stack>
                      </>
                    )}
                  </Box>
                </Stack>
                {selectedProfileTab === "broker" ? (
                  <Paper
                    elevation={0}
                    sx={{
                      ...tableCardSx,
                      mt: 2.5,
                    }}
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.75, sm: 2.5 }} sx={panelSectionBarSx}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: ui.inkSoft }}>Dealing Properties (2)</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: ui.inkSoft }}>Own Announcement Properties (3)</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: ui.inkSoft }}>User Announcement Properties (3)</Typography>
                    </Stack>
                    <TableContainer sx={tableContainerSx}>
                      <Table size="small" stickyHeader sx={tableSx}>
                        <TableHead sx={tableHeadRowSx}>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Property &amp; Broker</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Property Location</TableCell>
                            <TableCell>Price</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Date Listed</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[1, 2, 3, 4, 5].map((id) => (
                            <TableRow key={id} hover sx={tableRowHoverSx}>
                              <TableCell sx={tableBodyCellSx}>{id === 2 || id === 3 ? "PS-002" : "PS-001"}</TableCell>
                              <TableCell sx={tableBodyCellSx}>Lily W. Wilson</TableCell>
                              <TableCell sx={tableBodyCellSx}>{id % 2 ? "Land" : "Villa"}</TableCell>
                              <TableCell sx={tableBodyCellSx}>24 Hanover, New York...</TableCell>
                              <TableCell sx={tableBodyCellSx}>$180,000</TableCell>
                              <TableCell sx={tableBodyCellSx}>
                                <Chip
                                  label={id === 5 ? "Sold" : id % 2 ? "For Sell" : "For Rent"}
                                  size="small"
                                  sx={{ height: 26, fontSize: 13, fontWeight: 500 }}
                                />
                              </TableCell>
                              <TableCell sx={tableBodyCellSx}>Aug 30, 2024</TableCell>
                              <TableCell sx={tableBodyCellSx}>
                                <RemoveRedEyeOutlinedIcon sx={{ fontSize: 18, color: ui.iconView, cursor: "pointer" }} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Stack
                      direction="row"
                      justifyContent={{ xs: "center", sm: "flex-end" }}
                      flexWrap="wrap"
                      spacing={0.6}
                      sx={{ p: 1.5, borderTop: `1px solid ${ui.borderLight}`, bgcolor: ui.panelMuted }}
                    >
                      <Button size="small" variant="outlined" sx={{ minWidth: 52, fontSize: 13, textTransform: "none", borderColor: ui.border, color: ui.inkMuted }}>
                        Previous
                      </Button>
                      <Button size="small" variant="contained" color="primary" sx={{ minWidth: 32, fontSize: 13, textTransform: "none" }}>
                        1
                      </Button>
                      <Button size="small" variant="outlined" sx={{ minWidth: 32, fontSize: 13, textTransform: "none", borderColor: ui.border, color: ui.inkMuted }}>
                        2
                      </Button>
                      <Button size="small" variant="outlined" sx={{ minWidth: 32, fontSize: 13, textTransform: "none", borderColor: ui.border, color: ui.inkMuted }}>
                        3
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 44, fontSize: 13, textTransform: "none", borderColor: ui.successChipBorder, color: ui.successChipColor }}
                      >
                        Next
                      </Button>
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: ui.inkFaint, textAlign: "center", pb: 1.25, pt: 0.5 }}>2026 © Brokkerspot</Typography>
                  </Paper>
                ) : null}
              </>
            ) : (
              <Paper elevation={0} sx={tableCardSx}>
                <Box sx={panelSectionBarSx}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: ui.ink }}>Activity</Typography>
                  <Typography sx={{ fontSize: 12, color: ui.inkSoft, mt: 0.25 }}>Recent property-related actions</Typography>
                </Box>
                <TableContainer sx={tableContainerSx}>
                  <Table size="small" stickyHeader sx={tableSx}>
                    <TableHead sx={tableHeadRowSx}>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Property &amp; Broker</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Property For</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date Listed</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[1, 2, 3, 4].map((id) => (
                        <TableRow key={id} hover sx={tableRowHoverSx}>
                          <TableCell sx={tableBodyCellSx}>PS-001</TableCell>
                          <TableCell sx={tableBodyCellSx}>Lily W. Wilson</TableCell>
                          <TableCell sx={tableBodyCellSx}>Land</TableCell>
                          <TableCell sx={tableBodyCellSx}>$180,000</TableCell>
                          <TableCell sx={tableBodyCellSx}>
                            <Chip label={id % 2 ? "For Sell" : "For Rent"} size="small" sx={{ height: 26, fontSize: 13, fontWeight: 500 }} />
                          </TableCell>
                          <TableCell sx={tableBodyCellSx}>
                            <Chip label={id % 2 ? "Started" : "In Process"} size="small" sx={{ height: 26, fontSize: 13, fontWeight: 500 }} />
                          </TableCell>
                          <TableCell sx={tableBodyCellSx}>Aug 30, 2024</TableCell>
                          <TableCell sx={tableBodyCellSx}>
                            <ActionIcons />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Box>
        </Paper>
      ) : null}
    </Box>
  );
}

export function CustomerOverviewInfoPage() {
  return <OverviewLayout activity={false} />;
}

export function CustomerOverviewActivityPage() {
  return <OverviewLayout activity />;
}

export function UserApprovalsPage() {
  const { pageWrapSx } = useAdminUi();
  return (
    <Box sx={pageWrapSx}>
      <PageHeader title="Customers" crumbs={["Customers", "User Approvals"]} />
      <UserApprovalsList />
    </Box>
  );
}

/** Pending listings (`status=1`); approve / reject. */
export function AnnouncementApprovalPage() {
  const { pageWrapSx } = useAdminUi();
  return (
    <Box sx={pageWrapSx}>
      <PageHeader title="Announcement approval" crumbs={["Announcements", "Announcement approval"]} />
      <AnnouncementsList fetchStatus={1} />
    </Box>
  );
}

/** Live listings (`status=2`). Same list UI without moderation actions. */
export function ActiveAnnouncementsPage() {
  const { pageWrapSx } = useAdminUi();
  return (
    <Box sx={pageWrapSx}>
      <PageHeader title="Active announcements" crumbs={["Announcements", "Active announcements"]} />
      <AnnouncementsList fetchStatus={2} />
    </Box>
  );
}

export function ItemsPage() {
  const { pageWrapSx } = useAdminUi();
  return (
    <Box sx={pageWrapSx}>
      <PageHeader title="Items" crumbs={["Items"]} />
      <MasterDataSettings />
    </Box>
  );
}

export function GuestAnnouncementsPage() {
  const { pageWrapSx } = useAdminUi();
  return (
    <Box sx={pageWrapSx}>
      <PageHeader title="Guest announcements" crumbs={["Announcements", "Guest preview"]} />
      <GuestAnnouncementsList />
    </Box>
  );
}

export function PlaceholderPage({ title }) {
  const { pageWrapSx, tableCardSx, ui } = useAdminUi();
  return (
    <Box sx={pageWrapSx}>
      <PageHeader title={title} crumbs={[title]} />
      <Paper elevation={0} sx={{ ...tableCardSx, p: 3, color: ui.inkSoft }}>{title} page is wired and ready.</Paper>
    </Box>
  );
}
